import { useCallback, useRef, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

import { Trash2Icon, TagsIcon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";
import { css, styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import { TextArea } from "simplydo/react/ui/textarea";

import api from "api";

import { getImpactTypeIcon } from "components/lib/Ideas/ImpactStatements/impactIcons";

import type { IdeaPreviewImpactMeasure } from "../../types";

const impactDropdownClass = css`
  .menu.right {
    left: auto;
    right: 0;
  }
`;

const IdeaImpactHeader = styled.div`
  > span {
    display: block;
    margin: 0;
  }
  .impact-name {
  }
  .impact-description {
    opacity: 0.7;
  }
`;

const impactSegmentClass = css`
  > .row-flex {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }
`;

const ImpactFrequencyOptions = [
  { key: "weekly", text: "Weekly", value: "weekly" },
  { key: "monthly", text: "Monthly", value: "monthly" },
  { key: "annually", text: "Annually", value: "annually" },
  { key: "one-off", text: "One-off", value: "one-off" },
];

type ImpactProps = {
  idea: OpenAPI.Schemas["Idea"];
  onUpdateIdeaImpact: (
    impactMeasureId: string,
    data: Partial<OpenAPI.Schemas["IdeaImpact"]>,
  ) => void;
  deleteImpact: (impactId: string, impactMeasureId: string) => void;
  impact: IdeaPreviewImpactMeasure;
  onUpdateIdeaTotalImpacts?: (
    totalImpacts: OpenAPI.Schemas["IdeaImpact"][],
  ) => void;
  canEdit: boolean;
};

function Impact({
  idea,
  onUpdateIdeaImpact,
  impact,
  deleteImpact,
  onUpdateIdeaTotalImpacts = () => {},
  canEdit,
}: ImpactProps) {
  const ideaId = idea._id;
  const impactId = impact._id;
  const [impactData, setImpactData] =
    useState<OpenAPI.Schemas["IdeaImpact"]>(impact);
  const editTimeout = useRef<number>(null);
  const { t } = useTranslation();

  const saveImpactChanges = useThrottle(
    (data: Partial<OpenAPI.Schemas["IdeaImpact"]>) => {
      api.ideas.updateImpactMeasure(
        impact.idea,
        impact.impactId,
        impactId,
        data,
        (response: unknown) => {
          const totalImpacts = Array.isArray(
            (response as { impacts?: { totalImpacts?: unknown } }).impacts
              ?.totalImpacts,
          )
            ? (
                response as {
                  impacts: { totalImpacts: IdeaPreviewImpactMeasure[] };
                }
              ).impacts.totalImpacts
            : [];
          onUpdateIdeaTotalImpacts(totalImpacts);
        },
        (err) => {
          toast.error(err.message);
        },
      );
    },
    400,
    [ideaId, impact.impactId, impactId],
  );

  const handleIdeaUpdate = useCallback(
    (data: Partial<OpenAPI.Schemas["IdeaImpact"]>) => {
      onUpdateIdeaImpact(impactId, data);
      if (editTimeout.current) {
        clearTimeout(editTimeout.current);
        editTimeout.current = null;
      }
      editTimeout.current = setTimeout(() => saveImpactChanges(data), 400);
    },
    [impactId, onUpdateIdeaImpact, saveImpactChanges],
  );

  const updateImpact = useCallback(
    (data: Partial<OpenAPI.Schemas["IdeaImpact"]>) => {
      setImpactData((prev) => ({ ...prev, ...data }));
      saveImpactChanges(data);
      handleIdeaUpdate(data);
    },
    [saveImpactChanges, handleIdeaUpdate],
  );

  const formatGuidance = (type?: string) => {
    if (!type) return;
    if (type === "money") return "Pounds";
    if (type === "numerical") return "People";
    if (type === "time") return "Hours";
  };

  return (
    <Card fluid key={impactId}>
      <CardContent className={impactSegmentClass}>
        {impact.ownerIdea && idea._id !== impact.ownerIdea?._id ? (
          <Label style={{ marginBottom: 5 }}>
            This {t("generic.impact")} measure is shared from &quot;
            {impact.ownerIdea.name}&quot;
          </Label>
        ) : null}

        <div className="row-flex">
          <div
            style={{
              display: "flex",
              flex: 1,
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <Label style={{ display: "flex", alignItems: "center" }}>
              <Icon
                icon={getImpactTypeIcon(impact.ownerImpact?.type) ?? TagsIcon}
                size="large"
              />
              <IdeaImpactHeader>
                <span className="impact-name">{impact.ownerImpact?.name}</span>
                <span className="impact-description">
                  {impact.ownerImpact?.description}
                </span>
              </IdeaImpactHeader>
            </Label>
            {canEdit ? (
              <Button
                size="small"
                onClick={() => deleteImpact(impact.impactId, impactId)}
                style={{ marginLeft: 5 }}
                variant="outline"
              >
                <Icon icon={Trash2Icon} />
              </Button>
            ) : null}
          </div>
          <div />
        </div>

        {impact.ownerImpact?.type ? (
          <div className="row-flex">
            <div />
            <div style={{ display: "flex", flexDirection: "row" }}>
              {impact.ownerImpact.type === "dropdown" ? (
                <Dropdown
                  className={impactDropdownClass}
                  search
                  variant="outline"
                  value={impactData.value}
                  onChange={(_e, { value }) => {
                    updateImpact({
                      value:
                        typeof value === "string" || typeof value === "number"
                          ? value
                          : String(value ?? ""),
                    });
                  }}
                  disabled={!canEdit}
                  options={(impact.ownerImpact?.options || []).map(
                    (option: string, idx: number) => ({
                      key: idx,
                      value: option,
                      text: option,
                    }),
                  )}
                  direction="right"
                />
              ) : (
                <Input
                  data-tooltip={formatGuidance(impact.ownerImpact?.type)}
                  labelPosition="left"
                  size="small"
                  disabled={!canEdit}
                  onChange={(_e, { value }) => {
                    // @ts-ignore
                    if (!Number.isNaN(value)) {
                      // @ts-ignore
                      value = Number(value);
                    }
                    updateImpact({ value });
                  }}
                  value={impactData.value}
                >
                  <Label basic>
                    <Icon
                      icon={getImpactTypeIcon(impact.ownerImpact?.type)}
                      style={{
                        margin: 0,
                        padding: 0,
                        marginRight: impact.ownerImpact.type === "time" ? 5 : 0,
                      }}
                    />
                    {impact.ownerImpact.type === "time" ? "Hours" : null}
                    {impact.ownerImpact.effect === "cost" ? "-" : null}
                  </Label>
                  <input type="number" style={{ width: 80 }} />
                </Input>
              )}

              {impact.ownerImpact.type !== "dropdown" ? (
                <Dropdown
                  disabled={!canEdit}
                  variant="outline"
                  value={impactData.frequency}
                  onChange={(_e, { value }) =>
                    updateImpact({
                      frequency:
                        value as OpenAPI.Schemas["IdeaImpact"]["frequency"],
                    })
                  }
                  options={ImpactFrequencyOptions}
                  style={{ marginLeft: 5, width: "8em" }}
                  placeholder={`${t("common:capitalise", { key: "generic.impact" })} frequency`}
                />
              ) : null}
            </div>
          </div>
        ) : null}

        {impact.value && impact.frequency ? (
          <div className="row-flex">
            <div />
            <span style={{ display: "block" }}>
              This {t("generic.idea")} will{" "}
              {impact.ownerImpact?.effect === "cost" ? "cost" : "save"}{" "}
              {impact.value} {formatGuidance(impact.ownerImpact?.type)}{" "}
              {impact.frequency}
            </span>
          </div>
        ) : null}

        <TextArea
          disabled={!canEdit}
          value={impactData.justification}
          onChange={(e, { value }) =>
            updateImpact({
              justification:
                typeof value === "string" ? value : String(value ?? ""),
            })
          }
          placeholder={`Justify this ${t("generic.impact")} measure...`}
          rows={2}
        />
      </CardContent>
    </Card>
  );
}

const mapStateToProps = (state: { user: unknown }) => ({ user: state.user });

export default connect(mapStateToProps)(Impact);
